import {
  doc,
  getDocFromServer,
  Transaction,
  WriteBatch,
  UpdateData,
  setDoc,
  DocumentSnapshot,
} from 'firebase/firestore'
import firestore from '../../firestore'
import {
  Company,
  CompanyType,
  EstimatedProjectValueBand,
  PlanningApplicationStatus,
  PlanningApplicationType,
  PlanningCategory,
  PlanningClassification,
  PostcodeDistrict,
  allPlanningApplicationStatuses,
  allPlanningApplicationTypes,
  allPlanningCategories,
  allPlanningClassifications,
  allEstimatedProjectValueBands,
} from '@leadgen/models/client'
import BaseService from '../BaseService'

const createPlanningCategorySelectionMap = (
  selectedPlanningCategories: readonly PlanningCategory[] = [],
): { [K in PlanningCategory]: boolean } =>
  Object.fromEntries(
    allPlanningCategories.map(planningCategory => [
      planningCategory,
      selectedPlanningCategories.includes(planningCategory),
    ]),
  ) as { [K in PlanningCategory]: boolean }

const createPlanningClassificationSelectionMap = (
  selectedPlanningClassifications: readonly PlanningClassification[] = [],
): { [K in PlanningClassification]: boolean } =>
  Object.fromEntries(
    allPlanningClassifications.map(planningClassification => [
      planningClassification,
      selectedPlanningClassifications.includes(planningClassification),
    ]),
  ) as { [K in PlanningClassification]: boolean }

const createPlanningApplicationTypeSelectionMap = (
  selectedPlanningApplicationTypes: readonly PlanningApplicationType[] = [],
): { [K in PlanningApplicationType]: boolean } =>
  Object.fromEntries(
    allPlanningApplicationTypes.map(planningApplicationType => [
      planningApplicationType,
      selectedPlanningApplicationTypes.includes(planningApplicationType),
    ]),
  ) as { [K in PlanningApplicationType]: boolean }

const createPlanningApplicationStatusSelectionMap = (
  selectedPlanningApplicationStatuses: readonly PlanningApplicationStatus[] = [],
): { [K in PlanningApplicationStatus]: boolean } =>
  Object.fromEntries(
    allPlanningApplicationStatuses.map(planningApplicationStatus => [
      planningApplicationStatus,
      selectedPlanningApplicationStatuses.includes(planningApplicationStatus),
    ]),
  ) as { [K in PlanningApplicationStatus]: boolean }

const createEstimatedProjectValueBandSelectionMap = (
  selectedEstimatedProjectValueBands: readonly EstimatedProjectValueBand[] = [],
): { [K in EstimatedProjectValueBand]: boolean } =>
  Object.fromEntries(
    allEstimatedProjectValueBands.map(estimatedProjectValueBand => [
      estimatedProjectValueBand,
      selectedEstimatedProjectValueBands.includes(estimatedProjectValueBand),
    ]),
  ) as { [K in EstimatedProjectValueBand]: boolean }

class CompanyService extends BaseService {
  subscribeToCompany = async ({
    companyId,
    onCompanyUpdated,
  }: {
    companyId: string
    onCompanyUpdated: (company: Company) => void
  }) => {
    const { data, unsubscribe } = await this.onDoc({
      ref: doc(firestore.companies(), companyId),
      onUpdated: onCompanyUpdated,
      forceServerResponse: true,
    })
    if (data == null) {
      throw new Error("Company doesn't exist")
    }
    return {
      data,
      unsubscribe,
    }
  }

  fetchCompany = async ({
    companyId,
    transaction,
  }: {
    transaction?: Transaction
    companyId: string
  }): Promise<Company> => {
    const companyRef = doc(firestore.companies(), companyId)
    let companyDoc: DocumentSnapshot
    if (transaction) {
      companyDoc = await transaction.get(companyRef)
    } else {
      companyDoc = await getDocFromServer(companyRef)
    }
    if (!companyDoc.exists()) {
      throw new Error("Company doesn't exist")
    }
    return companyDoc.data() as Company
  }

  approveCompanyTemplatePendingReview = ({
    transactionOrBatch,
    companyId,
    templateVersion,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    companyId: string
    templateVersion: number
  }): void | Promise<void> => {
    const companiesRef = firestore.companies()
    const companyRef = doc(companiesRef, companyId)
    const companyUpdate: UpdateData<Company> = {
      pendingReviewTemplateVersion: null,
      pendingReviewTemplateFeedback: null,
      defaultTemplateVersion: templateVersion,
      hasDefaultTemplate: true,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      return
    }
    return setDoc(companyRef, companyUpdate, { merge: true })
  }
  declineCompanyTemplatePendingReview = ({
    transactionOrBatch,
    companyId,
    pendingReviewTemplateFeedback,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    companyId: string
    pendingReviewTemplateFeedback: string
  }): void | Promise<void> => {
    const companiesRef = firestore.companies()
    const companyRef = doc(companiesRef, companyId)
    const companyUpdate: UpdateData<Company> = {
      pendingReviewTemplateFeedback,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      return
    }
    return setDoc(companyRef, companyUpdate, { merge: true })
  }

  updateCompanyPendingReviewTemplate = ({
    transactionOrBatch,
    companyId,
    pendingReviewTemplateVersion,
    pendingReviewTemplateFeedback,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    companyId: string
    pendingReviewTemplateVersion: number
    pendingReviewTemplateFeedback?: string | null
  }): void | Promise<void> => {
    const companiesRef = firestore.companies()
    const companyRef = doc(companiesRef, companyId)
    const companyUpdate: UpdateData<Company> = {
      pendingReviewTemplateVersion,
      pendingReviewTemplateFeedback: pendingReviewTemplateFeedback || null,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      return
    }
    return setDoc(companyRef, companyUpdate, { merge: true })
  }
  updateStripePaymentMethod = ({
    transactionOrBatch,
    companyId,
    stripePaymentMethodId,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    companyId: string
    stripePaymentMethodId: string | null
  }): void | Promise<void> => {
    const companiesRef = firestore.companies()
    const companyRef = doc(companiesRef, companyId)
    const companyUpdate: UpdateData<Company> = {
      stripePaymentMethodId,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      return
    }
    return setDoc(companyRef, companyUpdate, { merge: true })
  }
  updateMonthlyCreditLimit = ({
    transactionOrBatch,
    companyId,
    monthlyCreditLimit,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    companyId: string
    monthlyCreditLimit: number
  }): void | Promise<void> => {
    const companiesRef = firestore.companies()
    const companyRef = doc(companiesRef, companyId)
    const companyUpdate: UpdateData<Company> = {
      monthlyCreditLimit,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      return
    }
    return setDoc(companyRef, companyUpdate, { merge: true })
  }
  updateLastChangedTemplate = ({
    transactionOrBatch,
    companyId,
    lastChangedTemplateVersion,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    companyId: string
    lastChangedTemplateVersion: number
  }): void | Promise<void> => {
    const companiesRef = firestore.companies()
    const companyRef = doc(companiesRef, companyId)
    const companyUpdate: UpdateData<Company> = {
      lastChangedTemplateVersion,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      return
    }
    return setDoc(companyRef, companyUpdate, { merge: true })
  }

  updateFilters = ({
    transactionOrBatch,
    companyId,
    companyType,
    planningCategories,
    planningClassifications,
    planningApplicationTypes,
    planningApplicationStatuses,
    estimatedProjectValueBands,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    companyId: string
    companyType: CompanyType
    planningCategories: PlanningCategory[]
    planningClassifications: PlanningClassification[]
    planningApplicationTypes: PlanningApplicationType[]
    planningApplicationStatuses: PlanningApplicationStatus[]
    estimatedProjectValueBands: EstimatedProjectValueBand[]
  }): void | Promise<void> => {
    const companiesRef = firestore.companies()
    const companyRef = doc(companiesRef, companyId)
    const companyUpdate: UpdateData<Company> = {
      companyType,
      planningCategories,
      _planningCategories: createPlanningCategorySelectionMap(planningCategories),
      planningClassifications,
      _planningClassifications: createPlanningClassificationSelectionMap(planningClassifications),
      planningApplicationTypes,
      _planningApplicationTypes:
        createPlanningApplicationTypeSelectionMap(planningApplicationTypes),
      planningApplicationStatuses,
      _planningApplicationStatuses:
        createPlanningApplicationStatusSelectionMap(planningApplicationStatuses),
      _estimatedProjectValueBands: createEstimatedProjectValueBandSelectionMap(
        estimatedProjectValueBands,
      ),
      estimatedProjectValueBands,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      return
    }
    return setDoc(companyRef, companyUpdate, { merge: true })
  }

  updatePostcodeDistricts = ({
    transactionOrBatch,
    companyId,
    postcodeDistricts,
  }: {
    transactionOrBatch?: WriteBatch | Transaction
    companyId: string
    postcodeDistricts: PostcodeDistrict[]
  }): void | Promise<void> => {
    const companiesRef = firestore.companies()
    const companyRef = doc(companiesRef, companyId)
    const companyUpdate: UpdateData<Company> = {
      postcodeDistricts,
    }
    if (transactionOrBatch) {
      // todo: for some reason I cant do this without type checking despite both transaction and writeBatch having the same function signature for update
      if (transactionOrBatch instanceof Transaction) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      if (transactionOrBatch instanceof WriteBatch) {
        transactionOrBatch.update(companyRef, companyUpdate)
      }
      return
    }
    return setDoc(companyRef, companyUpdate, { merge: true })
  }
}

export default new CompanyService()
